import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Map, Sparkles, PenLine, Plus, Trash2, Star, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GOAL_CATEGORIES, OWNER_TYPES, HORIZONS } from "@/lib/careerConfig";
import { translateFr as tfr } from "@/i18n";

function EmptyState({ onManual, onSuggested, loadingSuggestion }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-8 md:p-12 text-center max-w-2xl mx-auto" data-testid="career-empty-state">
      <span className="mx-auto h-14 w-14 rounded-2xl bg-athlynx/10 flex items-center justify-center" aria-hidden="true">
        <Map className="h-7 w-7 text-athlynx" />
      </span>
      <h2 className="text-xl font-semibold mt-5">{tfr("Aucun plan de carrière")}</h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
        Définissez la vision, la feuille de route et les objectifs de développement de cet athlète.
        Vous pouvez partir d'une page blanche ou de suggestions basées sur les données réelles du profil.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
        <Button variant="outline" onClick={onManual} className="h-11 gap-2" data-testid="career-create-manual">
          <PenLine className="h-4 w-4" /> Créer manuellement
        </Button>
        <Button onClick={onSuggested} disabled={loadingSuggestion} className="h-11 gap-2 bg-athlynx hover:bg-athlynx/90 text-white" data-testid="career-create-suggested">
          <Sparkles className="h-4 w-4" />
          {loadingSuggestion ? tfr("Préparation…") : tfr("Partir des suggestions")}
        </Button>
      </div>
    </div>
  );
}

function MilestoneEditorRow({ m, index, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2" data-testid={`editor-milestone-row-${index}`}>
      <Input
        value={m.title}
        onChange={(e) => onChange({ ...m, title: e.target.value })}
        placeholder={tfr("Titre du jalon")}
        maxLength={200}
        className="flex-1 h-9"
        aria-label={`Titre du jalon ${index + 1}`}
        data-testid={`editor-milestone-title-${index}`}
      />
      <div className="flex items-center gap-1 shrink-0">
        <span className="text-[11px] text-muted-foreground font-mono">M+</span>
        <Input
          type="number" min={0} max={120}
          value={m.target_month}
          onChange={(e) => onChange({ ...m, target_month: e.target.value })}
          className="w-16 h-9"
          aria-label={`${tfr("Échéance du jalon")} ${index + 1}`}
          data-testid={`editor-milestone-month-${index}`}
        />
      </div>
      <label className="flex items-center gap-1 shrink-0 cursor-pointer" title={tfr("Jalon clé")}>
        <Checkbox checked={!!m.is_key} onCheckedChange={(v) => onChange({ ...m, is_key: !!v })} aria-label={`${tfr("Jalon clé")} ${index + 1}`} />
        <Star className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      </label>
      <button onClick={onRemove} className="rounded-md p-1.5 text-muted-foreground hover:text-danger transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
        aria-label={`${tfr("Supprimer le jalon")} ${index + 1}`} data-testid={`editor-milestone-remove-${index}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function GoalEditorRow({ g, index, onChange, onRemove }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`editor-goal-row-${index}`}>
      <Input
        value={g.title}
        onChange={(e) => onChange({ ...g, title: e.target.value })}
        placeholder={tfr("Titre de l'objectif")}
        maxLength={200}
        className="flex-1 h-9"
        aria-label={`Titre de l'objectif ${index + 1}`}
        data-testid={`editor-goal-title-${index}`}
      />
      <div className="flex items-center gap-2">
        <Select value={g.category} onValueChange={(v) => onChange({ ...g, category: v })}>
          <SelectTrigger className="w-[150px] h-9 text-xs" aria-label={`${tfr("Catégorie de l'objectif")} ${index + 1}`} data-testid={`editor-goal-category-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GOAL_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={g.owner_type} onValueChange={(v) => onChange({ ...g, owner_type: v })}>
          <SelectTrigger className="w-[100px] h-9 text-xs" aria-label={`Responsable de l'objectif ${index + 1}`} data-testid={`editor-goal-owner-${index}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OWNER_TYPES.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={onRemove} className="rounded-md p-1.5 text-muted-foreground hover:text-danger transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40"
          aria-label={`${tfr("Supprimer l'objectif")} ${index + 1}`} data-testid={`editor-goal-remove-${index}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function PlanEditor({ playerId, initial, origin, disclaimer, onCancel, onCreated }) {
  const [vision, setVision] = useState(initial?.vision || "");
  const [horizon, setHorizon] = useState(String(initial?.horizon_months || 36));
  const [contractEnd, setContractEnd] = useState("");
  const [milestones, setMilestones] = useState(
    (initial?.milestones || []).map((m, i) => ({ _key: `m${i}`, title: m.title, target_month: m.target_month, is_key: !!m.is_key, status: m.status || "upcoming" }))
  );
  const [goals, setGoals] = useState(
    (initial?.goals || []).map((g, i) => ({ _key: `g${i}`, title: g.title, category: g.category || "other", owner_type: g.owner_type || "agent" }))
  );
  const [saving, setSaving] = useState(false);

  const addMilestone = () => setMilestones((ms) => [...ms, { _key: `m${Date.now()}`, title: "", target_month: 12, is_key: false, status: "upcoming" }]);
  const addGoal = () => setGoals((gs) => [...gs, { _key: `g${Date.now()}`, title: "", category: "performance", owner_type: "agent" }]);

  const save = async () => {
    const cleanMs = milestones.filter((m) => m.title.trim());
    const cleanGoals = goals.filter((g) => g.title.trim());
    setSaving(true);
    try {
      await api.post(`/players/${playerId}/career-plan`, {
        vision: vision.trim(),
        horizon_months: Number(horizon),
        contract_end_date: contractEnd || null,
        origin,
        milestones: cleanMs.map((m) => ({ title: m.title.trim(), target_month: Number(m.target_month) || 0, is_key: m.is_key, status: m.status })),
        goals: cleanGoals.map((g) => ({ title: g.title.trim(), category: g.category, owner_type: g.owner_type, progress: 0 })),
      });
      toast.success(tfr("Plan de carrière créé"));
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tfr("Impossible de créer le plan de carrière"));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 md:p-7 max-w-3xl mx-auto" data-testid="career-plan-editor">
      <h2 className="text-lg font-semibold">
        {origin === "suggested" ? tfr("Vérifier et ajuster le plan suggéré") : tfr("Créer le plan de carrière")}
      </h2>
      {disclaimer && (
        <div className="flex items-start gap-2 rounded-xl border border-info/30 bg-info/10 p-3 mt-3" data-testid="suggestion-disclaimer">
          <Info className="h-4 w-4 text-info shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-foreground/80">{disclaimer} Rien n'est enregistré tant que vous ne confirmez pas.</p>
        </div>
      )}
      <div className="space-y-5 mt-5">
        <div className="space-y-1.5">
          <Label htmlFor="plan-vision">{tfr("Vision carrière")} <span className="text-danger">*</span></Label>
          <Textarea
            id="plan-vision"
            value={vision}
            onChange={(e) => setVision(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={tfr("Ex. Devenir titulaire en Ligue 2 d'ici 24 mois, puis viser la Ligue 1…")}
            data-testid="career-editor-vision"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>{tfr("Horizon du plan")}</Label>
            <Select value={horizon} onValueChange={setHorizon}>
              <SelectTrigger data-testid="career-editor-horizon"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HORIZONS.map((h) => <SelectItem key={h} value={String(h)}>{h} mois</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan-contract-end">{tfr("Fin de contrat actuelle (optionnel)")}</Label>
            <Input id="plan-contract-end" type="date" value={contractEnd} onChange={(e) => setContractEnd(e.target.value)} data-testid="career-editor-contract-end" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>{tfr("Jalons de la feuille de route")}</Label>
            <Button variant="ghost" size="sm" onClick={addMilestone} className="h-7 gap-1 text-xs" data-testid="editor-add-milestone">
              <Plus className="h-3.5 w-3.5" /> Jalon
            </Button>
          </div>
          {milestones.length === 0 ? (
            <p className="text-xs text-muted-foreground">{tfr("Aucun jalon — vous pourrez en ajouter plus tard.")}</p>
          ) : (
            <div className="space-y-2">
              {milestones.map((m, i) => (
                <MilestoneEditorRow
                  key={m._key} m={m} index={i}
                  onChange={(nm) => setMilestones((ms) => ms.map((x, xi) => (xi === i ? { ...nm, _key: x._key } : x)))}
                  onRemove={() => setMilestones((ms) => ms.filter((_, xi) => xi !== i))}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>{tfr("Objectifs de développement")}</Label>
            <Button variant="ghost" size="sm" onClick={addGoal} className="h-7 gap-1 text-xs" data-testid="editor-add-goal">
              <Plus className="h-3.5 w-3.5" /> Objectif
            </Button>
          </div>
          {goals.length === 0 ? (
            <p className="text-xs text-muted-foreground">{tfr("Aucun objectif — vous pourrez en ajouter plus tard.")}</p>
          ) : (
            <div className="space-y-2">
              {goals.map((g, i) => (
                <GoalEditorRow
                  key={g._key} g={g} index={i}
                  onChange={(ng) => setGoals((gs) => gs.map((x, xi) => (xi === i ? { ...ng, _key: x._key } : x)))}
                  onRemove={() => setGoals((gs) => gs.filter((_, xi) => xi !== i))}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-7 pt-5 border-t border-border">
        <Button variant="ghost" onClick={onCancel} data-testid="career-editor-cancel">{tfr("Annuler")}</Button>
        <Button
          onClick={save}
          disabled={saving || !vision.trim()}
          className="bg-athlynx hover:bg-athlynx/90 text-white gap-2"
          data-testid="career-editor-save"
        >
          {saving ? tfr("Enregistrement…") : tfr("Enregistrer le plan")}
        </Button>
      </div>
    </div>
  );
}

export function PlanCreationFlow({ playerId, onCreated }) {
  const [mode, setMode] = useState(null); // null | "manual" | "suggested"
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);

  const startSuggested = async () => {
    setLoadingSuggestion(true);
    try {
      const r = await api.get(`/players/${playerId}/career-plan/suggestion`);
      setSuggestion(r.data);
      setMode("suggested");
    } catch { toast.error(tfr("Impossible de générer les suggestions")); }
    finally { setLoadingSuggestion(false); }
  };

  if (!mode) {
    return <EmptyState onManual={() => setMode("manual")} onSuggested={startSuggested} loadingSuggestion={loadingSuggestion} />;
  }
  return (
    <PlanEditor
      playerId={playerId}
      origin={mode}
      initial={mode === "suggested" ? suggestion : null}
      disclaimer={mode === "suggested" ? (suggestion?.disclaimer || tfr("Suggestions générées à partir des informations disponibles — vérifiez et modifiez avant d'enregistrer.")) : null}
      onCancel={() => { setMode(null); setSuggestion(null); }}
      onCreated={onCreated}
    />
  );
}
